import {
  PointSymbol,
  Measurement,
  MeasurementsCapabilityProvider,
  PolylineMeasurementType,
  MeasurementInfoButtonConfig,
  LineStyle,
} from '@trimble/web3d-providers';
import {
  PolygonCapability,
  PolylineMeasurementCapability,
} from '@trimble/web3d-capabilities';
import {
  Component,
  createEffect,
  createSignal,
  Show,
  on,
  onCleanup,
} from 'solid-js';
import { AngleUnit, AreaUnit, LengthUnit } from '@fulcrum/lib';
import { t } from '@trimble/i18n';
import { PanelLayout } from '@tcweb/connect-ui';
import { Button, IconButton, BtnClasses } from '@trimble/modus-ui';
import { Tab } from '@trimble-oss/modus-web-components/loader';
import { AlignmentSettings, FormattingSettings } from '@trimble/types';
import { ModusEditableTitle } from '@trimble/ui-components/solidjs';
import {
  getMeasurementConfig,
  isSupportedMeasurementType,
  MeasurementConfig,
} from './measurement-registry';
import { MeasurementStyle } from '@trimble/measurements-shared';
import type { PointMeasurementController } from './point/point-measurement.types';
import type { PolylineMeasurementController } from './polyline/polyline-measurement.types';
import type { AreaMeasurementController } from './area/area-measurement.types';
import type { AlignmentMeasurementController } from './alignment/alignment-measurement.types';
import { PointMeasurementDetails } from './point/PointMeasurementDetails';
import { PointMeasurementOptions } from './point/PointMeasurementOptions';
import { PolylineMeasurementDetails } from './polyline/PolylineMeasurementDetails';
import { PolylineMeasurementOptions } from './polyline/PolylineMeasurementOptions';
import { LineMeasurementDetails } from './line/LineMeasurementDetails';
import { AngleMeasurementDetails } from './angle/AngleMeasurementDetails';
import { AlignmentMeasurementDetails } from './alignment/AlignmentMeasurementDetails';
import { AlignmentMeasurementOptions } from './alignment/AlignmentMeasurementOptions';
import { RgbColor } from 'solid-color';
import { AreaMeasurementDetails } from './area/AreaMeasurementDetails';
import { AngleMeasurementController } from './angle';
import { LineMeasurementController } from './line/line-measurement.types';

type MeasurementController =
  | PointMeasurementController
  | PolylineMeasurementController
  | LineMeasurementController
  | AreaMeasurementController
  | AngleMeasurementController
  | AlignmentMeasurementController;

export type MeasurementSurfaceInfo = { id: string; name?: string };

export interface MeasurementDetailsPanelProps {
  measurement: Measurement | undefined;
  measurementsProvider: MeasurementsCapabilityProvider;
  polylineMeasurementCapability?: PolylineMeasurementCapability;
  baseLengthUnit: LengthUnit;
  baseAreaUnit: AreaUnit;
  angleUnit: AngleUnit;
  formattingSettings: FormattingSettings;
  onClose(): void;
  onSave?(): void;
  autoSave?: boolean;
  pointSymbols?: PointSymbol[];
  isMeasurementSaved?: boolean;
  measurementInfoConfig?: MeasurementInfoButtonConfig;
  hideTitleEdit?: boolean;
  disableEdit?: boolean;
  polygonCapability?: PolygonCapability;
  alignmentSettings?: AlignmentSettings;
  disabledReason?: string;
}

export const MeasurementDetailsPanel: Component<
  MeasurementDetailsPanelProps
> = (props) => {
  const [showConfig, setShowConfig] = createSignal(true);
  const [selectedTab, setSelectedTab] = createSignal('properties');
  const [controller, setController] = createSignal<
    MeasurementController | undefined
  >();

  const config = (): MeasurementConfig | undefined => {
    const type = props.measurement?.type;
    return type && isSupportedMeasurementType(type)
      ? getMeasurementConfig(type)
      : undefined;
  };

  createEffect(
    on(
      () => props.measurement?.type,
      (type, prevType) => {
        if (type !== prevType) {
          controller()?.cleanup();

          if (type && isSupportedMeasurementType(type)) {
            const cfg = getMeasurementConfig(type);
            const newController = cfg.createController(
              props.measurementsProvider
            );

            if (props.measurement) {
              newController.initialize(props.measurement);
            }
            setController(newController as MeasurementController);
            setSelectedTab('properties');
            setShowConfig(true);
          } else {
            setController(undefined);
          }
        }
      }
    )
  );

  onCleanup(() => {
    controller()?.cleanup();
  });

  createEffect(
    on(
      () => props.measurement,
      (measurement, prevMeasurement) => {
        if (
          measurement &&
          controller() &&
          measurement.id !== prevMeasurement?.id
        ) {
          controller()!.initialize(measurement);
        }
      }
    )
  );

  const tabs = (): Tab[] => [
    {
      id: 'properties',
      label: t('siteVisualization.properties'),
      active: selectedTab() === 'properties',
    },
    {
      id: 'style',
      label: t('measurements.style'),
      active: selectedTab() === 'style',
    },
  ];

  const handleMeasurementDetailsClose = (): void => {
    props.measurementsProvider.setSelectedItemId(null);
    props.onClose();
  };

  const persistSavedMeasurementIfNeeded = (): void => {
    if (props.autoSave !== false || !props.isMeasurementSaved) {
      return;
    }
    props.onSave?.();
  };

  const handleMeasurementLabelChange = (label: string | undefined): void => {
    controller()?.setLabel(label);
    persistSavedMeasurementIfNeeded();
  };

  const handleEditMeasurementCancel = (): void => {
    props.measurementsProvider.setSelectedItemId(null);
  };

  const handleEditMeasurementSave = (): void => {
    controller()?.update();
    props.onSave?.();
    props.measurementsProvider.setSelectedItemId(null);
  };

  const handlePointSymbolChange = (symbol: PointSymbol | undefined): void => {
    const ctrl = controller();
    if (ctrl?.type === 'point') {
      (ctrl as PointMeasurementController).setSelectedPointSymbol(symbol);
      persistSavedMeasurementIfNeeded();
    }
  };

  const handlePolylineMeasurementSave = (): void => {
    controller()?.update();
    persistSavedMeasurementIfNeeded();
  };

  const handleStyleColorChange = (color: RgbColor): void => {
    controller()!.setColor(color);
    persistSavedMeasurementIfNeeded();
  };

  const handleStyleFillOpacityChange = (value: number): void => {
    const ctrl = controller();
    if (ctrl?.type === 'area') {
      (ctrl as AreaMeasurementController).setFillOpacity(value);
      persistSavedMeasurementIfNeeded();
    }
  };

  const handleStyleLineThicknessChange = (value: number): void => {
    const ctrl = controller();
    if (ctrl?.type === 'area') {
      (ctrl as AreaMeasurementController).setLineThickness(value);
      persistSavedMeasurementIfNeeded();
    }
  };

  const handleStyleLineStyleChange = (value: LineStyle): void => {
    const ctrl = controller();
    if (ctrl?.type === 'area') {
      (ctrl as AreaMeasurementController).setLineStyle(value);
      persistSavedMeasurementIfNeeded();
    }
  };

  return (
    <div data-testid="measurement-details-panel" class="contents">
      <PanelLayout
        customTitle={
          <div class="flex items-center w-full gap-1">
            <ModusEditableTitle
              label={controller()?.label() ?? t('measurements.measurementData')}
              handleEditLabelSave={handleMeasurementLabelChange}
              hideTitleEdit={props.hideTitleEdit}
              disableEdit={props.disableEdit}
              disabledReason={props.disabledReason}
            />
            <IconButton
              color={BtnClasses.Tertiary}
              icon="tc-icon-content-close"
              size="icon-medium"
              data-cy="measurement-details-close"
              clicked={handleMeasurementDetailsClose}
              title={t('general.close')}
              classes={'!w-6 !h-6'}
            />
          </div>
        }
        content={
          <Show when={props.measurement && controller() && config()}>
            <div data-testid="measurement-details" class="flex flex-col h-full">
              <div class="flex items-center justify-between px-4 py-2 shrink-0">
                <span
                  class="font-semibold text-gray-8"
                  data-testid="configure-header"
                  textContent={t('measurements.configure')}
                />
                <i
                  class={`icon-font i32 cursor-pointer ${showConfig() ? 'tc-icon-arrow-drop-up' : 'tc-icon-arrow-drop-down'}`}
                  on:click={() => setShowConfig(!showConfig())}
                  data-testid="configure-toggle"
                />
              </div>

              <Show when={showConfig()}>
                <Show
                  when={
                    props.measurement?.type === 'point' &&
                    controller()?.type === 'point'
                  }
                >
                  <div data-testid="point-measurement-options" class="shrink-0">
                    <PointMeasurementOptions
                      controller={controller() as PointMeasurementController}
                      onConfigChange={persistSavedMeasurementIfNeeded}
                      disableEdit={props.disableEdit}
                      disabledReason={props.disabledReason}
                    />
                  </div>
                </Show>
                <Show
                  when={
                    props.measurement?.type === 'polyline' &&
                    controller()?.type === 'polyline'
                  }
                >
                  <div class="shrink-0">
                    <PolylineMeasurementOptions
                      controller={controller() as PolylineMeasurementController}
                      onConfigChange={persistSavedMeasurementIfNeeded}
                      disableEdit={props.disableEdit}
                      disabledReason={props.disabledReason}
                    />
                  </div>
                </Show>
                <Show
                  when={
                    props.measurement?.type === 'alignment' &&
                    controller()?.type === 'alignment'
                  }
                >
                  <div
                    data-testid="alignment-measurement-options"
                    class="shrink-0"
                  >
                    <AlignmentMeasurementOptions
                      controller={
                        controller() as AlignmentMeasurementController
                      }
                    />
                  </div>
                </Show>
              </Show>

              <div class="w-full pt-1 shrink-0" data-testid="tabs-container">
                <modus-tabs
                  size="small"
                  full-width={true}
                  tabs={tabs()}
                  on:tabChange={(e) => setSelectedTab(e.detail)}
                  data-testid="measurement-tabs"
                />
              </div>

              <div class="flex-1 overflow-y-auto min-h-0">
                <Show when={selectedTab() === 'properties'}>
                  <Show when={props.measurement?.type === 'point'}>
                    <PointMeasurementDetails
                      measurement={props.measurement}
                      baseLengthUnit={props.baseLengthUnit}
                      formattingSettings={props.formattingSettings}
                      infoButtonConfig={props.measurementInfoConfig}
                    />
                  </Show>
                  <Show when={props.measurement?.type === 'polyline'}>
                    <PolylineMeasurementDetails
                      measurement={props.measurement}
                      slopeFormat={
                        (props.measurement as PolylineMeasurementType)
                          ?.slopeFormat ||
                        props.polylineMeasurementCapability?.slopeFormat()
                      }
                      baseLengthUnit={props.baseLengthUnit}
                      formattingSettings={props.formattingSettings}
                      onSave={handlePolylineMeasurementSave}
                      infoButtonConfig={props.measurementInfoConfig}
                      disableEdit={props.disableEdit}
                      disabledReason={props.disabledReason}
                    />
                  </Show>
                  <Show when={props.measurement?.type === 'line'}>
                    <LineMeasurementDetails
                      measurement={props.measurement}
                      baseLengthUnit={props.baseLengthUnit}
                      formattingSettings={props.formattingSettings}
                      infoButtonConfig={props.measurementInfoConfig}
                    />
                  </Show>
                  <Show when={props.measurement?.type === 'angle'}>
                    <AngleMeasurementDetails
                      measurement={props.measurement}
                      angleUnit={props.angleUnit}
                      formattingSettings={props.formattingSettings}
                      infoButtonConfig={props.measurementInfoConfig}
                    />
                  </Show>
                  <Show when={props.measurement?.type === 'alignment'}>
                    <AlignmentMeasurementDetails
                      measurement={props.measurement}
                      baseLengthUnit={props.baseLengthUnit}
                      formattingSettings={props.formattingSettings}
                      alignmentSettings={props.alignmentSettings}
                      infoButtonConfig={props.measurementInfoConfig}
                    />
                  </Show>
                  <Show when={props.measurement?.type === 'area'}>
                    <AreaMeasurementDetails
                      measurement={props.measurement}
                      baseAreaUnit={props.baseAreaUnit}
                      baseLengthUnit={props.baseLengthUnit}
                      formattingSettings={props.formattingSettings}
                      infoButtonConfig={props.measurementInfoConfig}
                    />
                  </Show>
                </Show>

                <Show when={selectedTab() === 'style'}>
                  <MeasurementStyle
                    disabled={props.disableEdit}
                    disabledReason={props.disabledReason}
                    color={controller()!.color()}
                    onColorChange={handleStyleColorChange}
                    pointSymbols={
                      props.measurement?.type === 'point'
                        ? props.pointSymbols || []
                        : []
                    }
                    selectedPointSymbol={
                      controller()?.type === 'point'
                        ? (
                            controller() as PointMeasurementController
                          ).selectedPointSymbol()
                        : undefined
                    }
                    onPointSymbolChange={handlePointSymbolChange}
                    fillOpacity={
                      controller()?.type === 'area'
                        ? (
                            controller() as AreaMeasurementController
                          ).fillOpacity()
                        : undefined
                    }
                    lineStyle={
                      controller()?.type === 'area'
                        ? (
                            controller() as AreaMeasurementController
                          ).lineStyle()
                        : undefined
                    }
                    lineThickness={
                      controller()?.type === 'area'
                        ? (
                            controller() as AreaMeasurementController
                          ).lineThickness()
                        : undefined
                    }
                    onFillOpacityChange={
                      controller()?.type === 'area'
                        ? handleStyleFillOpacityChange
                        : undefined
                    }
                    onLineThicknessChange={
                      controller()?.type === 'area'
                        ? handleStyleLineThicknessChange
                        : undefined
                    }
                    onLineStyleChange={
                      controller()?.type === 'area'
                        ? handleStyleLineStyleChange
                        : undefined
                    }
                  />
                </Show>
              </div>

              <Show when={!props.isMeasurementSaved}>
                <div class="border-t border-gray-0 p-3 flex justify-end gap-2 shrink-0">
                  <Button
                    color={BtnClasses.Tertiary}
                    size="medium"
                    type="solid"
                    content={t('general.cancel')}
                    onClick={handleEditMeasurementCancel}
                    data-cy="measurement-details-cancel"
                  />
                  <Button
                    color={BtnClasses.Primary}
                    size="medium"
                    type="solid"
                    content={t('general.save')}
                    onClick={handleEditMeasurementSave}
                    data-cy="measurement-details-save"
                  />
                </div>
              </Show>
            </div>
          </Show>
        }
      />
    </div>
  );
};
